import { XHRRequestCanceler } from '../../Libs/xhr/XHRRequestCanceler';
import { BaseApiDataResponse } from '../Models/ApiData';
import {
    PointageListResponse,
    PointageSignatureListApiDataResponse,
    PointageSorts,
    PointageToAddResponse,
    SetPointageRequest,
} from '../Models/Pointage/Pointage';
import { BaseDataService } from './Base/BaseDataService';

export const PointageEndpoints = {
    POINTAGES: (week_date: string) => `/api/v1/back/pointages/weeks/${week_date}`,
    POINTAGES_TO_ADD: `/api/v1/back/pointages/to_add`,
    SET_POINTAGE: (affaire_id: string, day_date: string, compagnon_id: string) =>
        `/api/v1/back/pointages/affaires/${affaire_id}/date/${day_date}/compagnons/${compagnon_id}`,
    SET_COMMENT: (week_date: string) => `/api/v1/back/pointages/weeks/${week_date}/comment`,
    GET_SIGNATURES: (compagnon_id: string) =>
        `/api/v1/back/compagnons/${compagnon_id}/pointages/signatures`,
    SET_SIGNATURE_COMMENT: (compagnon_id: string, signature_id: string) =>
        `/api/v1/back/compagnons/${compagnon_id}/pointages/signatures/${signature_id}`,
    SET_POINTAGE_VALIDATION: (affaire_id: string, week_date: string, compagnon_id: string) =>
        `/api/v1/back/pointages/weeks/${week_date}/affaires/${affaire_id}/compagnons/${compagnon_id}/validation/validate`,
    SET_POINTAGE_COMMENT: (affaire_id: string, week_date: string, compagnon_id: string) =>
        `/api/v1/back/pointages/weeks/${week_date}/affaires/${affaire_id}/compagnons/${compagnon_id}/validation/comment`,
};

export interface IPointageDataService {
    setValidationCheckMark: (
        requestParams: {
            is_validated: boolean;
            week_date: string;
            affaire_id: string;
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;
    setValidationComment: (
        requestParams: {
            comment: string;
            week_date: string;
            affaire_id: string;
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;
    getPointages: (
        requestParams: {
            weekDate: string;
            sorts?: PointageSorts;
            affaire_id?: string;
            compagnon_id?: string;
            charge_affaire_id?: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<PointageListResponse>;

    setPointage: (
        requestParams: {
            weekDate: string;
            affaire_id: string;
            compagnon_id: string;
            setPointageRequest: SetPointageRequest;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    setComment: (
        requestParams: {
            weekDate: string;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;
    getPointagesSignature: (
        requestParams: {
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<PointageSignatureListApiDataResponse>;

    setPointageSignatureComment: (
        requestParams: {
            compagnon_id: string;
            signature_id: string;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    getPointageToAdd: (cts: XHRRequestCanceler) => Promise<PointageToAddResponse>;
}

class PointageDataService extends BaseDataService implements IPointageDataService {
    public getPointageToAdd = async (cts: XHRRequestCanceler) => {
        const response = await this.get<PointageToAddResponse>(
            PointageEndpoints.POINTAGES_TO_ADD,
            cts
        );
        return response;
    };

    public getPointages = async (
        requestParams: {
            weekDate: string;
            sorts?: PointageSorts;
            affaire_id?: string;
            compagnon_id?: string;
            charge_affaire_id?: string;
        },
        cts: XHRRequestCanceler
    ) => {
        return this.get<PointageListResponse>(
            PointageEndpoints.POINTAGES(requestParams.weekDate),
            cts,
            {
                params: {
                    sorts: requestParams.sorts,
                    affaire_id: requestParams.affaire_id,
                    compagnon_id: requestParams.compagnon_id,
                    charge_affaire_id: requestParams.charge_affaire_id,
                },
            }
        );
    };

    public setPointage = async (
        requestParams: {
            weekDate: string;
            affaire_id: string;
            compagnon_id: string;
            setPointageRequest: SetPointageRequest;
        },
        cts: XHRRequestCanceler
    ) => {
        const body = {
            ...requestParams.setPointageRequest,
        };
        return this.put<BaseApiDataResponse>(
            PointageEndpoints.SET_POINTAGE(
                requestParams.affaire_id,
                requestParams.weekDate,
                requestParams.compagnon_id
            ),
            body,
            cts
        );
    };

    public setComment = async (
        requestParams: {
            weekDate: string;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => {
        const body = {
            comment: requestParams.comment,
        };
        return this.put<BaseApiDataResponse>(
            PointageEndpoints.SET_COMMENT(requestParams.weekDate),
            body,
            cts
        );
    };

    public getPointagesSignature = async (
        requestParams: { compagnon_id: string },
        cts: XHRRequestCanceler
    ) => {
        return this.get<PointageSignatureListApiDataResponse>(
            PointageEndpoints.GET_SIGNATURES(requestParams.compagnon_id),
            cts
        );
    };

    public setPointageSignatureComment = async (
        requestParams: { compagnon_id: string; signature_id: string; comment: string },
        cts: XHRRequestCanceler
    ) => {
        return this.patch(
            PointageEndpoints.SET_SIGNATURE_COMMENT(
                requestParams.compagnon_id,
                requestParams.signature_id
            ),
            { comment: requestParams.comment },
            cts
        );
    };

    public setValidationCheckMark = async (
        requestParams: {
            is_validated: boolean;
            week_date: string;
            affaire_id: string;
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => {
        const { affaire_id, week_date, compagnon_id } = requestParams;
        const body = {
            is_validated: requestParams.is_validated,
        };
        return this.put<BaseApiDataResponse>(
            PointageEndpoints.SET_POINTAGE_VALIDATION(affaire_id, week_date, compagnon_id),
            body,
            cts
        );
    };

    public setValidationComment = async (
        requestParams: {
            comment: string;
            week_date: string;
            affaire_id: string;
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => {
        const { affaire_id, week_date, compagnon_id } = requestParams;
        const body = {
            comment: requestParams.comment,
        };
        return this.put<BaseApiDataResponse>(
            PointageEndpoints.SET_POINTAGE_COMMENT(affaire_id, week_date, compagnon_id),
            body,
            cts
        );
    };
}

const PointageDataServiceName = 'PointageDataService';
export { PointageDataService, PointageDataServiceName };
